
import React from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Loader from '../common/Spinner';

const REDIRECT_FLAG = 'ee.seenProfileIncompleteNotice';

// Return TRUE only when we're confident the profile needs completion.
const needsProfileCompletion = (profile) => {
  const p = profile || {};

  // 1) Explicit backend booleans take precedence
  if (p.profileCompleted === true || p.isProfileComplete === true) return false;
  if (p.profileCompleted === false || p.isProfileComplete === false) return true;

  // 2) Status strings (adjust to your API if needed)
  if (typeof p.profileStatus === 'string') {
    const s = p.profileStatus.toLowerCase();
    if (s === 'complete' || s === 'completed' || s === 'verified' || s === 'approved') return false;
    if (s === 'incomplete' || s === 'required' || s === 'pending') return true;
  }

  // 3) Fallback: check a minimal set of required fields.
  const required = ['firstName', 'lastName', 'email'];
  let present = 0;
  for (let i = 0; i < required.length; i++) {
    const key = required[i];
    const val = p[key];
    if (typeof val === 'string' && val.trim().length > 0) present++;
  }

  // If fewer than 2 of 3 are present, it *likely* needs completion (fresh user).
  return present < 2;
};

const PrivateRoute = () => {
  const auth = useSelector((s) => s.auth);
  const location = useLocation();

  const authChecked = !!auth.authChecked;
  const isAuthenticated = !!auth.isAuthenticated;
  const user = auth.user || {};
  const userprofile = auth.userprofile || {};
  // Gate ONLY on the one-time bootstrap flag, not on `loading`/
  // `profileloading` — those flip on/off for every unrelated auth thunk
  // (attachEmail, verifyAccount, attachWallet, ...) fired by pages
  // rendered under this route, and gating here on them was unmounting
  // whatever page was on screen (e.g. Complete Your Profile mid-OTP-entry)
  // every time one of those fired, wiping its local form state. See
  // authChecked in reducers/authReducer.js.
  if (!authChecked) return <Loader />;

  // Must be logged in and correct role
  const isUser = isAuthenticated === true && user.userType === 'user';
  if (!isUser) {
    // Clean up the flag if user logs out / is not a user
    try { sessionStorage.removeItem(REDIRECT_FLAG); } catch {}
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // Allowlist pages that should not be blocked
  const BYPASS_PATHS = ['/user/complete-profile', '/user/logout'];
  const onBypassPath = BYPASS_PATHS.indexOf(location.pathname) !== -1;

  // BUG-35 fix: this used to run needsProfileCompletion() unconditionally,
  // so a profile that simply hadn't finished its very first fetch yet
  // (still {}), or whose last fetch failed (network hiccup — see
  // getProfile()'s catch in reducers/authReducer.js), looked exactly like
  // a genuinely-empty new-user profile and redirected a complete account
  // straight to /user/complete-profile. Wait for an in-flight fetch, and
  // don't redirect off the back of a failed one — only a load that
  // actually succeeded is trusted to decide "incomplete".
  if (!onBypassPath) {
    if (auth.profileloading) return <Loader />;
    if (auth.profileLoadFailed) return <Outlet />;
  }

  const incomplete = needsProfileCompletion(userprofile);

  // If profile is complete, ensure the flag is cleared for future sessions
  if (!incomplete) {
    try { sessionStorage.removeItem(REDIRECT_FLAG); } catch {}
    return <Outlet />;
  }

  // Profile is incomplete and we're on a protected /user route (not bypass)
  // — send straight to complete-profile. (Previously this bounced to Home
  // once first, silently, before sending to complete-profile on the next
  // attempt — which meant a brand-new wallet user's very first login just
  // dumped them back on Home with no visible explanation.)
  if (!onBypassPath) {
    try { sessionStorage.removeItem(REDIRECT_FLAG); } catch {}
    return <Navigate to="/user/complete-profile" replace state={{ from: location }} />;
  }

  return <Outlet />;
};

export default PrivateRoute;